import InitApi from "api/InitApi";
import ServerErrors from "components/SeverErrors";
import toast from "react-hot-toast";
import { useMutation, useQueryClient } from "react-query";
import { useHistory } from "react-router-dom";
import { reactLocalStorage } from "reactjs-localstorage";
import useTokenStore from "store/tokenStore";
import { deleteEmptyObjects } from "utils";
import XLSX from "xlsx";

const emptySummary = () => ({
	total: 0,
	successful: 0,
	failed: 0,
	failures: [],
});

const cell = (row, names) => {
	const keys = Object.keys(row || {});
	for (const name of names) {
		const needle = name.toLowerCase().replace(/\s+/g, "");
		const found = keys.find(
			(key) => key.toLowerCase().replace(/\s+/g, "") === needle
		);
		if (found != null && row[found] !== undefined && row[found] !== "") {
			return row[found];
		}
	}
	return "";
};

const parseImportResultBlob = async (blob) => {
	try {
		const buffer = await blob.arrayBuffer();
		const workbook = XLSX.read(new Uint8Array(buffer), { type: "array" });
		const sheetName = workbook.SheetNames[0];
		if (!sheetName) return emptySummary();

		const rows = XLSX.utils.sheet_to_json(workbook.Sheets[sheetName], {
			defval: "",
		});
		const failures = [];
		let successful = 0;

		rows.forEach((row, index) => {
			const status = String(cell(row, ["Status"])).toLowerCase();
			if (status === "success" || status === "successful") {
				successful += 1;
				return;
			}
			failures.push({
				rowNumber: index + 2,
				title: String(cell(row, ["Title"]) || `Row ${index + 2}`),
				reason: String(cell(row, ["Reason"]) || "Unknown error"),
			});
		});

		return {
			total: rows.length,
			successful,
			failed: failures.length,
			failures,
		};
	} catch {
		return emptySummary();
	}
};

const parseBlobError = async (error) => {
	let message = "Failed to import books";
	let data = null;
	let unauthenticated = false;

	if (error?.response?.data instanceof Blob) {
		try {
			const json = JSON.parse(await error.response.data.text());
			message = json?.message || message;
			data = json?.data || null;
			unauthenticated = json?.message === "Unauthenticated.";
		} catch {
			// blob was not JSON
		}
	} else {
		message = error?.response?.data?.message || message;
		data = error?.response?.data?.data || null;
		unauthenticated = error?.response?.data?.message === "Unauthenticated.";
	}

	return { message, data, unauthenticated };
};

const useImportBooks = () => {
	const queryClient = useQueryClient();
	const { accessToken } = useTokenStore();

	const history = useHistory();

	const importBooks = async (obj) => {
		const instance = await InitApi({ accessToken }, "multipart/form-data");

		const body = deleteEmptyObjects(obj);

		const formData = new FormData();
		Object.keys(body).forEach((key) => {
			formData.append(key, body[key]);
		});

		await queryClient.invalidateQueries("books");
		await queryClient.invalidateQueries("myUploadedBooks");

		const response = await instance.post("/books/import", formData, {
			responseType: "blob",
		});

		const blob = new Blob([response.data], {
			type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
		});
		const url = window.URL.createObjectURL(blob);

		let filename = "books_import_result.xlsx";
		const contentDisposition = response.headers["content-disposition"];
		if (contentDisposition) {
			const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
			if (filenameMatch) {
				filename = filenameMatch[1];
			}
		}

		queryClient.invalidateQueries("books");
		queryClient.invalidateQueries("myUploadedBooks");

		const summary = await parseImportResultBlob(blob);

		return { downloadUrl: url, filename, summary };
	};

	return useMutation(importBooks, {
		onError: async (error) => {
			const { message, data, unauthenticated } = await parseBlobError(error);

			toast.error(
				<ServerErrors errorMessage={message} errors={data} />,
				{
					className: "max-w-max",
				}
			);

			if (unauthenticated) {
				reactLocalStorage.clear();
				history.push("/");
			}
		},
	});
};

export default useImportBooks;
